/**
 * @file src/local/page/LocalFilesBrowser.tsx
 * @description Every file this device is holding for you, as a list you can sort and clear out.
 *
 * ONE browser, wherever it is shown. It is the whole of the `local.html` page (which the main site
 * frames, because that storage is origin-scoped and root cannot read it), and it is also the body
 * of the studio's own local-files panel. Two copies of this would be two sets of columns, two
 * removal confirmations and two ideas of what a collection is called — which is exactly the split
 * this component exists to avoid.
 *
 * ## What is measured and what is remembered
 *
 * The total at the top is `vaultUsageBytes()` — the disk asked again, never a running sum. A
 * number added up from intentions drifts from the disk it claims to describe the first time a
 * delete fails or a second tab imports, and this is the one surface whose whole job is to be right
 * about that.
 *
 * Sizes per row are the file's own size as the platform reported it at import, which is what a
 * person recognises; the two disagree by whatever the browser's own bookkeeping costs, so the
 * header says what the machine holds and the rows say how big the files are.
 *
 * ## Names, never ids
 *
 * A collection is called what its owner called it. Files imported before the library started
 * remembering names leave only an id, so those are looked up once and written down
 * (`resolveLocalScopeNames`); a collection the platform says is gone is LABELLED as gone, because
 * that is the case whose files are stranded and the reason this surface exists.
 *
 * ## Removal
 *
 * Everything here goes through `removeLocalEntry` and `removeLocalScope`, which already delete the
 * copy, abort a copy in flight and report honestly whether the bytes went. No second lifecycle.
 */
import type * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FolderMinus, FolderOpen, HardDrive, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  DataList,
  ToggleGroup,
  ToggleGroupItem,
  type DataListColumn,
  type DataListGroup,
} from 'plantasia.space-design/react';
import './localFilesPage.css';
import {
  LOCAL_STATUS,
  bootLocalLibrary,
  listAllLocalEntries,
  listLocalScopes,
  localScopeNameFailure,
  localStatusOf,
  refreshLocalCopiedBytes,
  removeLocalEntry,
  removeLocalScope,
  removeLocalEntryFromScope,
  resolveLocalScopeNames,
  findLocalDuplicates,
  scopesOf,
  subscribeToLocalLibrary,
} from '../localLibrary.js';
import { LocalDuplicatesNotice, type LocalDuplicateGroup } from './LocalDuplicatesNotice';
import { INLINE_SCOPE } from '../localAudioConfig.js';
import { fetchCollectionData } from '../../api/collectionDataService.js';
import { getT } from '../../i18n/index.js';

/** One collection's worth of what this machine is holding, for the "empty a collection" row. */
interface ScopeBucket {
  key: string | null;
  name: string | null;
  count: number;
  bytes: number;
  /** Why it has no name yet: 'gone' (the platform says it is deleted) or 'unreachable'. */
  nameFailure: string | null;
}

/** A React key for the bucket whose scope is `null`; no collection id can collide with it. */
const NO_COLLECTION_KEY = 'no-collection';



/**
 * What to call a collection. Never its id — an id is a fact about our database, not something a
 * person put a name to.
 *
 * When there is no name, the label says WHY, and the reasons are not interchangeable. "Deleted"
 * means those files are stranded and clearing them out is the only thing left to do with them;
 * "not yours to open" means the collection is alive and someone else's, and the files are fine;
 * "cannot be reached" means we know nothing at all and the next visit may well name it. Saying the
 * last of those about the first two is how a page tells somebody their audio is lost when it is not.
 */
function labelForScope(
  t: ReturnType<typeof getT>,
  key: string | null,
  name: string | null,
  nameFailure: string | null,
): string {
  if (key === null) return t('localFiles.noCollection', 'No collection') as string;
  if (key === INLINE_SCOPE) return t('localFiles.inlineScope', 'Outside a collection') as string;
  if (name) return name;
  if (nameFailure === 'gone') return t('localFiles.deletedCollection', 'Deleted collection') as string;
  if (nameFailure === 'no-access') {
    return t('localFiles.noAccessCollection', 'A collection this account cannot open') as string;
  }
  if (nameFailure === 'sign-in') {
    return t('localFiles.signInCollection', 'A collection that needs you signed in') as string;
  }
  if (nameFailure === 'unnamed') return t('localFiles.namelessCollection', 'A collection with no name') as string;
  return t('localFiles.unnamedCollection', 'A collection this browser cannot reach') as string;
}

interface LocalRowGroupFields {
  /** The label of the folder this membership row stands in. Only set in the grouped view. */
  groupLabel?: string | null;
}

interface LocalRow extends LocalRowGroupFields {
  /**
   * The row's key. In "everything" it is the entry id. Grouped by collection it is the entry id
   * PLUS the collection, because there the row is a MEMBERSHIP: one file in three collections is
   * three rows, one in each folder, exactly as an alias appears in three folders on a desktop.
   */
  id: string;
  /** The file behind the row. Several rows can share one — that is what an alias is. */
  entryId: string;
  /** The collection this row stands in, when grouped. Null in "everything", and for a file that
   *  belongs to no collection at all. */
  scope: string | null;
  name: string;
  size: number;
  addedAt: number;
  /** The collections holding it, by key. Empty means none — the file is only here. */
  scopes: string[];
  /** Their names, as they were when a file was last added to them. */
  scopeLabels: string[];
  kind: string;
  status: string;
  copied: boolean;
  /** The artist the file names, or null when it names none. The file name is still the row's name. */
  artist: string | null;
}

/**
 * Bytes as a person reads them.
 *
 * Finer than the downloads page's rounding, which stops at whole megabytes: this list is one row
 * per FILE, and a library of three-minute takes rendered as a column of "<1 MB" would sort
 * correctly and tell nobody anything.
 */
function formatBytes(bytes: number | null | undefined): string {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '0 KB';
  if (value < 1024) return `${value} B`;
  const kb = value / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/** The date column. Short, local, and sortable on the number underneath rather than this string. */
function formatDate(addedAt: number): string {
  if (!Number.isFinite(addedAt) || addedAt <= 0) return '—';
  try {
    return new Date(addedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

/** What the file is, from its name — the extension is what a person recognises the format by. */
function kindOf(name: string): string {
  const match = /\.([a-z0-9]{1,5})$/i.exec(name || '');
  return match ? match[1].toLowerCase() : '—';
}

/** The tone a state is worth saying in. Only two of them are news; the rest are the normal case. */
const STATE_TONE: Record<string, 'positive' | 'warning' | 'info' | undefined> = {
  [LOCAL_STATUS.MISSING]: 'warning',
  [LOCAL_STATUS.FAILED]: 'warning',
  [LOCAL_STATUS.PERMISSION]: 'warning',
  [LOCAL_STATUS.SESSION_ONLY]: 'warning',
  [LOCAL_STATUS.COPYING]: 'info',
  [LOCAL_STATUS.ANALYSING]: 'info',
  [LOCAL_STATUS.DEFERRED]: 'info',
  [LOCAL_STATUS.READY]: 'positive',
};

/**
 * A button that asks before it acts.
 *
 * Its own component so each one owns its dialog state — a single shared open flag would let a
 * confirmation opened for one thing be answered for another after a re-render. Used by removal and
 * by "empty a collection", so the two ask in exactly the same shape.
 */
function ConfirmButton({
  label,
  icon,
  busy,
  title,
  body,
  confirmLabel,
  cancelLabel,
  variant = 'outline',
  onConfirm,
}: {
  label: string;
  icon?: React.ReactNode;
  busy?: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  variant?: 'outline' | 'secondary';
  onConfirm: () => void;
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant={variant} size="sm" disabled={busy}>
          {icon}
          <span>{label}</span>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export interface LocalFilesBrowserProps {
  /**
   * Whether to draw the page's own heading. The studio panel supplies its own, and two headings
   * for one list read as two lists — the same reason root's frame draws none.
   */
  showTitle?: boolean;
  /** Extra class on the outer column, so a host can set its own padding and height. */
  className?: string;
  /**
   * Whether the list draws its own bordered frame. False inside a surface that is already a frame
   * — a box drawn inside a box is two boxes, and the panel is the box.
   */
  frame?: boolean;
  /**
   * Controlled selection, so a host can offer its OWN action on the chosen rows. The list keeps
   * exactly one action of its own (removal, which is destructive and belongs beside the rows);
   * anything additive is the host's, because only the host knows what adding would mean.
   */
  selected?: readonly string[];
  onSelectedChange?: (ids: string[]) => void;
  /**
   * Force the two-line row layout. A side panel is ALWAYS narrow — that is a fact about the
   * surface, not something to measure and hope about — so it says so instead of leaving `auto` to
   * work it out from a container width.
   */
  compactLayout?: boolean;
  /**
   * Dragging rows out of the list, handed straight to the list. Nothing here interprets it: where a
   * drag lands and what that means belong to whatever is hosting this, which is the only thing that
   * knows what is around it.
   */
  drag?: React.ComponentProps<typeof DataList<LocalRow>>['drag'];
  /**
   * One extra action on the selection, offered by whatever is hosting the list. It sits in the
   * same bar as removal so there is a single place where "what happens to the chosen files" lives.
   */
  hostAction?: {
    label: string;
    icon?: React.ReactNode;
    /**
     * The collection it adds to. Without it the list cannot know whether a selection is already
     * there — and offering "add" for files that are all in it already is offering something that
     * cannot happen.
     */
    targetScope?: string | null;
    onAction: (ids: string[]) => void | Promise<void>;
  } | null;
}

export function LocalFilesBrowser({
  showTitle = true,
  className,
  frame = true,
  selected,
  onSelectedChange,
  compactLayout = false,
  hostAction = null,
  drag,
}: LocalFilesBrowserProps) {
  const t = getT();
  const [rows, setRows] = useState<LocalRow[]>([]);
  const [scopeBuckets, setScopeBuckets] = useState<ScopeBucket[]>([]);
  const [usedBytes, setUsedBytes] = useState<number | null>(null);
  /** Files this device is holding twice, from before one entry could be in several collections. */
  const [duplicates, setDuplicates] = useState<LocalDuplicateGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'all' | 'collections'>('all');
  /** The collection whose "remove everything" is running, so it can be said and not repeated. */
  const [busyScope, setBusyScope] = useState<string | null>(null);
  // The chosen rows, when no host is controlling them. The action bar below needs to know the
  // selection whether the page or the panel owns it, so it reads one of the two.
  const [ownSelected, setOwnSelected] = useState<string[]>([]);
  const selectedIds = selected ?? ownSelected;
  const setSelectedIds = useCallback(
    (ids: string[]) => {
      setOwnSelected(ids);
      onSelectedChange?.(ids);
    },
    [onSelectedChange],
  );

  const read = useCallback(() => {
    setDuplicates(findLocalDuplicates() as LocalDuplicateGroup[]);
    const buckets = listLocalScopes();
    const names = new Map(buckets.map((bucket) => [bucket.key, bucket.name]));
    setScopeBuckets(
      buckets.map(({ key, name, entries, bytes }) => ({
        key,
        name,
        count: entries.length,
        bytes,
        nameFailure: key === null ? null : localScopeNameFailure(key),
      })),
    );
    setRows(
      listAllLocalEntries().map((entry) => {
        const keys: string[] = scopesOf(entry);
        return {
          id: entry.id,
          entryId: entry.id,
          scope: null,
          name: entry.name,
          size: Number(entry.size) || 0,
          addedAt: Number(entry.addedAt) || 0,
          scopes: keys,
          scopeLabels: keys.map((key) => labelForScope(t, key, names.get(key) ?? null, localScopeNameFailure(key))),
          kind: kindOf(entry.name),
          status: localStatusOf(entry.id) ?? LOCAL_STATUS.READY,
          copied: Boolean(entry.copied),
          artist: entry.tags?.artist ?? null,
        };
      }),
    );
  }, [t]);

  useEffect(() => {
    let active = true;
    // Closing the panel has to stop the work it started, not just stop listening to it: the name
    // lookups are one request per collection and they were still going after unmount, writing to
    // state nobody was watching.
    const abort = new AbortController();
    void (async () => {
      await bootLocalLibrary();
      if (!active) return;
      read();
      setLoading(false);
      const measured = await refreshLocalCopiedBytes();
      if (!active) return;
      setUsedBytes(measured);
      // Name every collection the library has files for. Only the ones still unnamed cost a
      // request, and the answer is written into the store — so this is once per collection ever,
      // not once per visit, and the page names them with no connection from then on.
      await resolveLocalScopeNames({ fetchCollection: fetchCollectionData, signal: abort.signal });
    })();
    // The library emits for every import, copy tick, analysis result and removal made in THIS
    // document. Nothing crosses tabs, here or in the studio — there is no channel to claim it does.
    // COALESCED, not called straight through. The library emits on every copy-progress tick,
    // every analysis result and every presence answer — importing a folder is thousands of those
    // — and each `read` walks the whole library twice (once for the rows, once to group the
    // duplicates) and redraws the list. One pass per frame is as often as a list of files can
    // usefully change, and this app's rules put the phone first.
    let frame = 0;
    const stop = subscribeToLocalLibrary(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (active) read();
      });
    });
    return () => {
      active = false;
      if (frame) cancelAnimationFrame(frame);
      abort.abort();
      stop();
    };
  }, [read]);

  /** Ask the disk again after anything that should have changed it. Measured, never assumed. */
  const remeasure = useCallback(async () => {
    setUsedBytes(await refreshLocalCopiedBytes());
  }, []);

  const scopeLabel = useCallback(
    (key: string | null, name: string | null, nameFailure: string | null = null) =>
      labelForScope(t, key, name, nameFailure),
    [t],
  );

  const columns = useMemo<DataListColumn<LocalRow>[]>(
    () => [
      {
        key: 'name',
        header: t('localFiles.column.name', 'Name'),
        width: 'minmax(180px,1fr)',
        cell: (row) => <span title={row.name}>{row.name}</span>,
        sortValue: (row) => row.name,
      },
      {
        // What the file says its artist is. Hideable, and empty for a file that says nothing —
        // which is most of a folder of takes, and not a state worth marking.
        key: 'artist',
        header: t('localFiles.column.artist', 'Artist'),
        width: 'minmax(100px,160px)',
        hideable: true,
        cell: (row) =>
          row.artist ? <span title={row.artist}>{row.artist}</span> : <span className="ps-local-muted">—</span>,
        sortValue: (row) => row.artist ?? '',
      },
      {
        key: 'collections',
        header: t('localFiles.column.collections', 'Collections'),
        width: 'minmax(120px,180px)',
        hideable: true,
        cell: (row) =>
          row.scopes.length === 0 ? (
            <span className="ps-local-muted">{t('localFiles.noCollection', 'No collection')}</span>
          ) : (
            // The names, not a count: "Field recordings, Drafts" answers the question a count
            // only raises. The count is what it SORTS on, so ordering by this column groups the
            // files that are in several — which is the reason to look at it at all.
            <span title={row.scopeLabels.join(', ')}>{row.scopeLabels.join(', ')}</span>
          ),
        sortValue: (row) => row.scopes.length,
      },
      {
        key: 'size',
        header: t('localFiles.column.size', 'Size'),
        width: '84px',
        align: 'end',
        cell: (row) => formatBytes(row.size),
        sortValue: (row) => row.size,
      },
      {
        key: 'added',
        header: t('localFiles.column.added', 'Date added'),
        shortHeader: t('localFiles.column.addedShort', 'Date'),
        width: '120px',
        hideable: true,
        cell: (row) => formatDate(row.addedAt),
        sortValue: (row) => row.addedAt,
      },
      {
        key: 'kind',
        header: t('localFiles.column.kind', 'Kind'),
        width: '64px',
        hideable: true,
        cell: (row) => row.kind,
        sortValue: (row) => row.kind,
      },
      {
        key: 'state',
        header: t('localFiles.column.state', 'State'),
        width: '128px',
        cell: (row) => (
          <Badge variant="secondary" tone={STATE_TONE[row.status]}>
            {t(`localFiles.state.${row.status}`, row.status)}
          </Badge>
        ),
        sortValue: (row) => row.status,
      },
    ],
    [t],
  );

  const total = useMemo(() => rows.reduce((sum, row) => sum + row.size, 0), [rows]);

  /**
   * What the list actually shows.
   *
   * "Everything" is one row per FILE. Grouped by collection it is one row per MEMBERSHIP, so a file
   * in two collections stands in both folders — an alias, the way it works on a desktop. Anything
   * else would make the folder view quietly untrue: it would file a shared audio under whichever
   * collection happened to come first and leave the other folder looking empty of it.
   *
   * The rows are still the same FILE underneath (`entryId`), which is what every action works on.
   */
  const viewRows = useMemo(() => {
    if (view !== 'collections') return rows;
    return rows.flatMap((row) =>
      (row.scopes.length ? row.scopes : [null]).map((scope, index) => ({
        ...row,
        id: scope === null ? row.id : `${row.id}::${scope}`,
        scope,
        // The group's own label, resolved once here so `groupOf` stays a lookup and not a decision.
        scopeLabels: row.scopeLabels,
        groupLabel: scope === null ? null : (row.scopeLabels[index] ?? scope),
      })),
    );
  }, [rows, view]);
  // The chosen rows, resolved from ids against the CURRENT rows — so a file removed elsewhere
  // drops out of the action bar instead of leaving it counting something that is gone.
  const selectedRows = useMemo(() => {
    const chosen = new Set(selectedIds);
    return viewRows.filter((row) => chosen.has(row.id));
  }, [viewRows, selectedIds]);

  /**
   * The FILES behind the selection, each once.
   *
   * In the folder view a selection can hold two rows that are the same file in two collections.
   * Every action is about the file, so they are deduplicated here — and the counts a confirmation
   * quotes are file counts, never row counts, or somebody would be told they are removing two
   * things when there is one.
   */
  const selectedFiles = useMemo(() => {
    const seen = new Map<string, LocalRow>();
    for (const row of selectedRows) if (!seen.has(row.entryId)) seen.set(row.entryId, row);
    return [...seen.values()];
  }, [selectedRows]);

  /**
   * The one collection every selected row stands in, or null when they are spread across several
   * (or the view is not grouped). It is what makes "take these out of THIS collection" a sentence
   * that can be said truthfully — with rows from three folders selected, there is no "this".
   */
  const selectedScope = useMemo(() => {
    if (view !== 'collections' || !selectedRows.length) return null;
    const first = selectedRows[0].scope;
    if (first === null) return null;
    return selectedRows.every((row) => row.scope === first) ? { key: first, label: selectedRows[0].groupLabel ?? first } : null;
  }, [selectedRows, view]);
  const onDevice = useMemo(() => rows.filter((row) => row.copied).length, [rows]);

  /**
   * Row ids → the files behind them, deduplicated.
   *
   * A row id is this component's own business: in the folder view it is `<entry>::<collection>`,
   * because one file standing in two folders is two rows. Nothing outside knows that shape, so
   * every id handed out — to a host's drag handlers, to the library — is translated here first.
   * Getting this wrong is silent: the library simply does not find the entry and does nothing.
   */
  const toEntryIds = useCallback(
    (ids: readonly string[]): string[] => {
      const byRow = new Map(viewRows.map((row) => [row.id, row.entryId]));
      const out: string[] = [];
      for (const id of ids) {
        const entryId = byRow.get(id);
        if (entryId && !out.includes(entryId)) out.push(entryId);
      }
      return out;
    },
    [viewRows],
  );

  /** The host's drag callbacks, given entry ids rather than this component's row ids. */
  const dragWithEntryIds = useMemo(() => {
    if (!drag) return undefined;
    return {
      ...drag,
      onDragStart: drag.onDragStart ? (ids: string[], event: PointerEvent) => drag.onDragStart?.(toEntryIds(ids), event) : undefined,
      onDragMove: drag.onDragMove ? (ids: string[], event: PointerEvent) => drag.onDragMove?.(toEntryIds(ids), event) : undefined,
      onDragEnd: drag.onDragEnd ? (ids: string[], event: PointerEvent) => drag.onDragEnd?.(toEntryIds(ids), event) : undefined,
    };
  }, [drag, toEntryIds]);

  const grouping = useMemo(
    () => ({
      // One group per collection, and a file in three collections is a row in all three — see
      // `viewRows`, which is where one file becomes one row per membership.
      groupOf: (row: LocalRow): DataListGroup | null =>
        row.scope === null ? null : { key: row.scope, label: row.groupLabel ?? row.scope },
      ungroupedLabel: t('localFiles.noCollection', 'No collection'),
      summary: (groupRows: LocalRow[]) =>
        `${t('localFiles.fileCount', { count: groupRows.length, defaultValue: '{{count}} files' })} · ${formatBytes(
          groupRows.reduce((sum, row) => sum + row.size, 0),
        )}`,
    }),
    [scopeLabel, t],
  );

  /**
   * Take a selection off the machine.
   *
   * A row here is an ENTRY, and an entry owns its bytes however many collections point at it — so
   * this is unambiguous whichever view it was chosen from, and it is the reason the model is one
   * entry with a set of collections rather than one entry per collection sharing a copy.
   */
  const removeRows = useCallback(
    async (chosen: LocalRow[]) => {
      // One at a time, not in parallel: each removal re-measures the whole store, and a hundred
      // concurrent scans of it is the shape that makes a phone stop responding.
      let kept = 0;
      for (const row of chosen) {
        const result = await removeLocalEntry(row.entryId);
        // A copy the browser would not release is still using the disk. The header re-measures, so
        // the number stays true either way — this is only so nobody has to work that out from it.
        if (result.removed && !result.copyDeleted) kept += 1;
      }
      await remeasure();
      if (kept) console.warn('[local] some copies could not be deleted from this browser', kept);
    },
    [remeasure],
  );

  const emptyScope = useCallback(
    async (key: string | null) => {
      setBusyScope(key ?? '');
      try {
        await removeLocalScope(key);
        await remeasure();
      } finally {
        setBusyScope(null);
      }
    },
    [remeasure],
  );


  /**
   * Everything that can happen to a selection, declared ONCE.
   *
   * The list renders these in its right-click menu and the bar below renders them as buttons, from
   * this same array — so the two can never drift into offering different things, or the same thing
   * under two names. Nothing is marked `primary`: the list's own strip would then show one action
   * and hide the rest behind a right-click, and both of these have to be visible at once.
   *
   * Every one of them acts on FILES, not rows: in the folder view a selection can hold the same
   * file twice, under two collections.
   */
  const fileActions = useMemo(() => {
    const built: Array<{
      key: string;
      label: string;
      icon: React.ReactNode;
      destructive?: boolean;
      applies?: () => boolean;
      confirm?: () => { title: string; body: string; confirmLabel: string; cancelLabel: string };
      run: () => void | Promise<void>;
    }> = [];

    if (hostAction) {
      built.push({
        key: 'add',
        label: hostAction.label,
        icon: hostAction.icon,
        // Offered only while there is something to add. Every chosen file already being in that
        // collection makes this an action with nothing to do, and an action with nothing to do
        // should not be on screen at all — which is different from being greyed out, and this is
        // not "the right action at the wrong moment".
        applies: () =>
          !hostAction.targetScope ||
          selectedFiles.some((row) => !row.scopes.includes(hostAction.targetScope as string)),
        run: () => hostAction.onAction(selectedFiles.map((row) => row.entryId)),
      });
    }

    // In a folder, "remove" most naturally means "take it out of this folder" — and the action
    // beside it means something far more final. So when every chosen row stands in ONE collection,
    // the gentler act is offered by name. A file taken out of its LAST collection does leave the
    // device, which `removeLocalEntryFromScope` decides, not this.
    if (selectedScope) {
      const scope = selectedScope;
      built.push({
        key: 'take-out',
        label: t('localFiles.takeOut', { name: scope.label, defaultValue: 'Take out of {{name}}' }) as string,
        icon: <FolderMinus aria-hidden="true" />,
        // The mirror of the one above: only while something chosen is actually IN it. Selecting a
        // file that is in this collection and one that is not offers both, which is correct —
        // each does its own half and neither is a lie.
        applies: () => selectedFiles.some((row) => row.scopes.includes(scope.key)),
        // Asked only when it would actually cost something. Leaving a collection loses nothing
        // while the file is in another one — but a file whose LAST collection this is leaves the
        // device with its bytes, and that is the same loss the button beside it warns about.
        confirm: !selectedFiles.some((row) => row.scopes.length === 1 && row.scopes[0] === scope.key)
          ? undefined
          : () => ({
              title: t('localFiles.takeOutDialog.title', {
                count: selectedFiles.length,
                name: scope.label,
                defaultValue: 'Take {{count}} files out of \u201c{{name}}\u201d?',
              }) as string,
              body: t('localFiles.takeOutDialog.body', {
                count: selectedFiles.length,
                defaultValue:
                  'They stay on this machine for any other collection that holds them. One that is in no other collection is removed from the device, and its space comes back.',
              }) as string,
              confirmLabel: t('localFiles.takeOutDialog.confirm', 'Take them out') as string,
              cancelLabel: t('localFiles.cancel', 'Cancel') as string,
            }),
        run: async () => {
          for (const row of selectedFiles) await removeLocalEntryFromScope(row.entryId, scope.key);
          await remeasure();
        },
      });
    }

    built.push({
      key: 'remove',
      label: t('localFiles.remove', 'Remove from device') as string,
      icon: <Trash2 aria-hidden="true" />,
      destructive: true,
      // Say what goes, what does not, and what it would take to get it back. These files were
      // never uploaded anywhere, so nothing can restore them but the person's own disk.
      confirm: () => ({
        title: t('localFiles.confirm.title', {
          count: selectedFiles.length,
          defaultValue: 'Remove {{count}} files from this device?',
        }) as string,
        body: t('localFiles.confirm.body', {
          count: selectedFiles.length,
          size: formatBytes(selectedFiles.reduce((sum, row) => sum + row.size, 0)),
          defaultValue:
            'This frees {{size}} here. The originals on your disk are untouched — nothing was ever uploaded — and adding them again brings them back, with their waveforms built afresh.',
        }) as string,
        confirmLabel: t('localFiles.remove', 'Remove from device') as string,
        cancelLabel: t('localFiles.cancel', 'Cancel') as string,
      }),
      run: () => removeRows(selectedFiles),
    });

    return built;
  }, [hostAction, remeasure, removeRows, selectedFiles, selectedScope, t]);

  /** Run one, clear the selection, and keep the bar disabled while it is in flight. */
  const runAction = useCallback(
    (action: { run: () => void | Promise<void> }) => {
      void Promise.resolve(action.run())
        .then(() => setSelectedIds([]))
        .catch((error) => {
          // Removals run one at a time, so a store that refuses halfway leaves some files gone and
          // some still here. Clearing the selection then would hide exactly which — so it is KEPT,
          // and the list re-reads from the store, which drops the ones that really went and leaves
          // the rest ticked. Swallowing this instead would be an unhandled rejection and a lie.
          console.warn('[local] an action on the selection did not finish', error);
          read();
        });
    },
    [read, setSelectedIds],
  );

  const header = !showTitle ? null : (
    <header className="ps-local-header">
      <h1 className="ps-local-title">
        {/* The herbarium set has no folder or disk glyph, and inventing one for a page that is
            not an entity would be a mark with no meaning elsewhere. The same lucide mark the view
            switch uses for "everything on this machine". */}
        <HardDrive style={{ height: 24, width: 24 }} aria-hidden="true" />
        <span>{t('localFiles.title', 'Local files')}</span>
      </h1>
      <p className="ps-local-note ps-local-muted">
        {usedBytes === null
          ? t('localFiles.measuring', 'Measuring what this machine is holding…')
          : t('localFiles.usage', {
              used: formatBytes(usedBytes),
              count: onDevice,
              defaultValue: '{{used}} on this machine, across {{count}} files kept here',
            })}
      </p>
    </header>
  );

  return (
  <div className={className ? `ps-local-page ${className}` : 'ps-local-page'}>
      {header}

      {/* A library imported before a file could belong to several collections holds some of them
          twice, with two copies of the bytes. The store's upgrade cannot merge those — that means
          deleting one of two entries — so it is offered here, to the person whose disk it is. */}
      <LocalDuplicatesNotice
        groups={duplicates}
        scopeLabel={(key) => {
          const bucket = scopeBuckets.find((candidate) => candidate.key === key);
          return scopeLabel(key, bucket?.name ?? null, bucket?.nameFailure ?? null);
        }}
        formatBytes={formatBytes}
        onMerged={remeasure}
      />

      <div className="ps-local-toolbar">
        <ToggleGroup
          type="single"
          size="sm"
          value={view}
          onValueChange={(next) => {
            // Radix clears the value when the pressed item is pressed again. A view switch has
            // no "neither", so an empty answer means "stay where you are".
            if (next === 'all' || next === 'collections') setView(next);
          }}
          aria-label={t('localFiles.view', 'View')}
        >
          <ToggleGroupItem value="all">
            <HardDrive aria-hidden="true" />
            {t('localFiles.viewAll', 'Everything')}
          </ToggleGroupItem>
          <ToggleGroupItem value="collections">
            <FolderOpen aria-hidden="true" />
            {t('localFiles.viewCollections', 'By collection')}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="ps-local-list">
        <DataList<LocalRow>
          rows={viewRows}
          rowId={(row) => row.id}
          columns={columns}
          // Nothing sorts implicitly, and the order rows arrive in is the order they were added.
          defaultSort={{ key: 'name', direction: 'asc' }}
          grouping={view === 'collections' ? grouping : undefined}
          selectionMode="multiple"
          selected={selectedIds}
          onSelectedChange={setSelectedIds}
          drag={dragWithEntryIds}
          // The same actions in the strip and on a right-click, from one declaration, so the two
          // can never offer different things or the same thing under two names.
          actions={fileActions.map((action) => ({
            key: action.key,
            // Both of them in the strip, side by side: the two visible peers, in one place, low.
            strip: true,
            label: action.label,
            icon: action.icon,
            destructive: action.destructive,
            applies: action.applies ? () => action.applies!() : undefined,
            confirm: action.confirm ? () => action.confirm!() : undefined,
            onAction: () => runAction(action),
          }))}
          layout={compactLayout ? 'compact' : 'auto'}
          frame={frame}
          loading={loading}
          compact={{
            primary: (row) => row.name,
            secondary: (row) =>
              row.scopes.length === 0 ? t('localFiles.noCollection', 'No collection') : row.scopeLabels.join(', '),
            trailing: (row) => formatBytes(row.size),
          }}
          selectionSummary={(chosen, count) =>
            `${t('localFiles.selected', { count: chosen.length, total: count, defaultValue: '{{count}} of {{total}} selected' })} · ${formatBytes(
              chosen.reduce((sum, row) => sum + row.size, 0),
            )}`
          }
          empty={{
            title: t('localFiles.empty.title', 'This device is holding nothing'),
            description: t(
              'localFiles.empty.description',
              'Files you drop into a collection are kept here so they are still there after a reload. None are yet.',
            ),
          }}
          caption={t('localFiles.caption', {
            count: rows.length,
            size: formatBytes(total),
            defaultValue: '{{count}} files · {{size}}',
          })}
          height="100%"
        />
      </div>

      {/* Emptying a whole collection is its own act, and it is the only way to reclaim the space
          of a collection that has been deleted — its files are valid entries pointing at
          something that is gone, so no rail will ever show them again. It lives here rather than
          on the group header: that header is one short line, truncates its summary, and is
          duplicated aria-hidden while it is pinned, so a control inside it would exist twice. */}
      {view === 'collections' && scopeBuckets.length ? (
        <footer className="ps-local-footer">
          <p className="ps-local-note ps-local-muted">{t('localFiles.emptyScopeLead', 'Clear out a whole group:')}</p>
          <div className="ps-local-scope-buttons">
            {scopeBuckets.map((bucket) => (
              <ConfirmButton
                key={bucket.key ?? NO_COLLECTION_KEY}
                label={scopeLabel(bucket.key, bucket.name, bucket.nameFailure)}
                icon={<Trash2 aria-hidden="true" />}
                busy={busyScope !== null}
                title={
                  bucket.key === null
                    ? (t('localFiles.emptyNoCollection.title', {
                        count: bucket.count,
                        defaultValue: 'Remove {{count}} files from this device?',
                      }) as string)
                    : (t('localFiles.emptyScope.title', {
                        name: scopeLabel(bucket.key, bucket.name, bucket.nameFailure),
                        defaultValue: 'Empty \u201c{{name}}\u201d?',
                      }) as string)
                }
                // Emptying a COLLECTION is not "delete these files": one that is also in another
                // collection only leaves this one, and its copy stays for the collection that
                // still plays it.
                //
                // The "no collection" bucket is the exception, and it is the destructive one. By
                // definition none of those files is in another collection, so every one of them
                // leaves the device with its bytes — and nothing can bring them back but the
                // person's own disk. The gentle wording would be false exactly where it matters.
                body={
                  bucket.key === null
                    ? (t('localFiles.emptyNoCollection.body', {
                        count: bucket.count,
                        size: formatBytes(bucket.bytes),
                        defaultValue:
                          'These belong to no collection, so all {{count}} of them leave this device, freeing {{size}}. The originals on your disk are untouched — nothing was ever uploaded — and adding them again brings them back, with their waveforms built afresh.',
                      }) as string)
                    : (t('localFiles.emptyScope.body', {
                        count: bucket.count,
                        size: formatBytes(bucket.bytes),
                        defaultValue:
                          '{{count}} files leave this collection, freeing up to {{size}}. Any of them that is also in another collection stays on this machine for that one. Your own files on disk are untouched.',
                      }) as string)
                }
                confirmLabel={
                  bucket.key === null
                    ? (t('localFiles.remove', 'Remove from device') as string)
                    : (t('localFiles.emptyScope.confirm', 'Empty it') as string)
                }
                cancelLabel={t('localFiles.cancel', 'Cancel')}
                onConfirm={() => void emptyScope(bucket.key)}
              />
            ))}
          </div>
        </footer>
      ) : null}
  </div>
  );
}
